import { injectable, inject } from 'inversify'
import type { $ZodIssue } from 'zod/v4/core'
import { labelKeyOf, type Schema } from './validation.schema'
import { ValidationMessages } from './validation.messages'
import type { ValidationIssue, ValidationResult } from './validation.types'

/**
 * Runs a schema and hands back the app's own result type, with every message
 * already translated. The one place zod's output shape is known.
 */
@injectable()
export class ValidationRunner {
  @inject(ValidationMessages)
  private validationMessages!: ValidationMessages

  validate<T>(schema: Schema<T>, value: unknown): ValidationResult<T> {
    const parsed = schema.safeParse(value)

    if (parsed.success) return { valid: true, value: parsed.data }

    return {
      valid: false,
      // One issue per field: zod reports every failed rule, but a form showing
      // "Password is required" and "Password is too short" for one empty box
      // is noise. First issue per path wins, which is the order the schema
      // declares its rules in.
      issues: this.firstPerPath(parsed.error.issues, schema),
    }
  }

  private firstPerPath(issues: $ZodIssue[], schema: Schema<unknown>): ValidationIssue[] {
    const seen = new Set<string>()

    return issues.flatMap((issue) => {
      const path = issue.path.join('.')
      if (seen.has(path)) return []
      seen.add(path)

      return [
        {
          path,
          message: this.validationMessages.messageFor(
            issue.code,
            labelKeyOf(this.fieldSchemaAt(schema, issue.path)),
            this.paramsFor(issue),
          ),
        },
      ]
    })
  }

  /** Walks into an object schema to find the field a path refers to. */
  private fieldSchemaAt(schema: Schema<unknown>, path: PropertyKey[]): Schema<unknown> | undefined {
    if (path.length === 0) return schema

    const shape = (schema as unknown as { shape?: Record<string, Schema<unknown>> }).shape
    return shape?.[String(path[0])]
  }

  private paramsFor(issue: $ZodIssue): Record<string, string | number> {
    if (issue.code === 'too_small') return { min: String(issue.minimum) }
    if (issue.code === 'too_big') return { max: String(issue.maximum) }
    return {}
  }
}
